import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
  type ReactNode,
} from "react";
import { api, type Principal } from "./client";
import { useQueryClient } from "@/api/zustandQuery";
import { serverQueryClient } from "@/api/serverQueryClient";
import { clearQueueReloadCache, startQueueReloadCache } from './queueReloadCache';
import { clearPageReloadCache, startPageReloadCache,principalReadIdentity } from './pageReloadCache';

type AuthStatus = "idle" | "loading" | "authenticated" | "unauthenticated" | "error";

export interface AuthState {
  status: AuthStatus;
  principal: Principal | null;
  accessToken: string | null;
  error: string | null;
  isLoading: boolean;
  isRestoring: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, mfaCode?: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

export const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [status, setStatus] = useState<AuthStatus>("idle");
  const [isRestoring, setIsRestoring] = useState(true);
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const principalScope = useRef('');
  const stopReloadCache = useRef<(() => void) | null>(null);
  const authAttempt=useRef(0);
  const changingAuth=useRef(false);
  const commitPrincipal = useCallback((next: Principal | null) => {
    const scope = principalReadIdentity(next);
    api.setReadScope(scope);
    stopReloadCache.current?.();
    stopReloadCache.current = null;
    if (principalScope.current !== scope) {
      // Cancel old-session continuations before mounting the next workspace.
      // This also scopes existing shared menus/settings/provider caches safely.
      queryClient.clear();
      serverQueryClient.clear();
      principalScope.current = scope;
    }
    if (next) {
      const stopQueue=startQueueReloadCache(queryClient,next);
      const stopPages=startPageReloadCache(queryClient,serverQueryClient,next);
      stopReloadCache.current=()=>{stopQueue();stopPages();};
    } else {clearQueueReloadCache();clearPageReloadCache();}
    setPrincipal(next);
  }, [queryClient]);

  useEffect(() => () => stopReloadCache.current?.(), []);

  const restore = useCallback(async () => {
    const attempt=++authAttempt.current;
    setStatus("loading");
    setError(null);
    try {
      // Try to refresh the session first so the API client has a valid access
      // token. This avoids relying solely on the httpOnly access-token cookie
      // and prevents confusing 401s when the cookie is not present.
      const refreshed = await api.refresh();
      if(attempt!==authAttempt.current)return;
      if (!refreshed) {
        throw new Error("No active session");
      }
      setAccessToken(api.getAccessToken());
      const me = import.meta.env.VITE_MCP_AUTH_BOOTSTRAP_FROM_REFRESH_ENABLED === 'true'
        ? api.getRefreshedPrincipal() ?? await api.me()
        : await api.me();
      if(attempt!==authAttempt.current)return;
      commitPrincipal(me);
      setStatus("authenticated");
    } catch (err) {
      if(attempt!==authAttempt.current)return;
      api.setAccessToken(null);
      commitPrincipal(null);
      setStatus("unauthenticated");
      // swallow expected unauthorized errors on restore
      if (err && typeof err === "object" && "status" in err && (err as { status?: number }).status !== 401) {
        setError(err instanceof Error ? err.message : "Session restore failed");
      }
    } finally {
      if(attempt===authAttempt.current)setIsRestoring(false);
    }
  }, [commitPrincipal]);

  useEffect(() => {
    void restore();
  }, [restore]);

  const login = useCallback(async (email: string, password: string, mfaCode?: string) => {
    const attempt=++authAttempt.current;changingAuth.current=true;
    setStatus("loading");
    setError(null);
    try {
      const result = await api.login(email, password, true, mfaCode);
      if(attempt!==authAttempt.current)return;
      api.setAccessToken(result.accessToken);
      setAccessToken(result.accessToken);
      commitPrincipal(result.user);
      setStatus("authenticated");
    } catch (err) {
      if(attempt!==authAttempt.current)return;
      api.setAccessToken(null);
      setAccessToken(null);
      commitPrincipal(null);
      setStatus("error");
      if (err && typeof err === "object" && "problem" in err) {
        const problem = (err as { problem?: { detail?: string; title?: string } }).problem;
        setError(problem?.detail || problem?.title || "Login failed");
      } else {
        setError(err instanceof Error ? err.message : "Login failed");
      }
      throw err;
    }finally{if(attempt===authAttempt.current){changingAuth.current=false;setIsRestoring(false);}}
  }, [commitPrincipal]);

  const logout = useCallback(async () => {
    const attempt=++authAttempt.current;changingAuth.current=true;
    try {
      await api.logout();
    } finally {
      if(attempt!==authAttempt.current)return;
      api.setAccessToken(null);
      setAccessToken(null);
      commitPrincipal(null);
      setStatus("unauthenticated");
      setError(null);
      changingAuth.current=false;setIsRestoring(false);
    }
  }, [commitPrincipal]);

  const refresh = useCallback(async () => {
    if(changingAuth.current)return;
    const attempt=++authAttempt.current;
    try {
      const refreshed = await api.refresh();
      if(attempt!==authAttempt.current)return;
      if (!refreshed) {
        throw new Error("Session refresh failed");
      }
      setAccessToken(api.getAccessToken());
      const me = import.meta.env.VITE_MCP_AUTH_BOOTSTRAP_FROM_REFRESH_ENABLED === 'true'
        ? api.getRefreshedPrincipal() ?? await api.me()
        : await api.me();
      if(attempt!==authAttempt.current)return;
      commitPrincipal(me);
      setStatus("authenticated");
    } catch {
      if(attempt!==authAttempt.current)return;
      api.setAccessToken(null);
      setAccessToken(null);
      commitPrincipal(null);
      setStatus("unauthenticated");
    }
  }, [commitPrincipal]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      principal,
      accessToken,
      error,
      isLoading: status === "loading" || status === "idle",
      isRestoring,
      isAuthenticated: status === "authenticated" && principal !== null,
      login,
      logout,
      refresh,
    }),
    [status, principal, accessToken, error, isRestoring, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function RequireAuth({ children }: { children: ReactNode }): ReactNode {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <AuthLoading />;
  if (!isAuthenticated) return <LoginRedirect />;
  return children;
}

function AuthLoading() {
  return (
    <div className="ev-auth-loading">
      <div className="ev-auth-loading__spinner" aria-label="Loading session" />
      <p>Restoring session…</p>
    </div>
  );
}

function LoginRedirect() {
  // Rendered outside router in this provider placement, so we rely on App.tsx to route
  return null;
}
